/**
 * hotspotOverrides.js — local-only working store for the Hotspot Editor.
 *
 * The committed source of truth is HOTSPOT_COORDS (src/content/hotspotCoords.js)
 * for geometry and the scene files (src/content/scenes/*.js) for hotspot
 * definitions (label / description / type). When the editor is enabled, edits
 * are kept in localStorage and merged OVER the committed data so the live
 * hotspots move — and newly authored inspection targets appear and respond — in
 * real time while you calibrate. Nothing here runs for a normal viewer:
 * isHotspotEditorEnabled() is false in production unless the explicit flag is
 * set, so the merge helpers return committed data unchanged. Editing here never
 * ships; you copy the result out of the editor and paste it into hotspotCoords.js
 * (geometry) and the scene file (new hotspot definitions) to make it real.
 *
 * Store shape (v2):
 *   { [sceneId]: { coords:  { [id]: {left,top,width,height,cursor} },
 *                  added:   { [id]: {label, description} },
 *                  removed: [ id, ... ],
 *                  edited:  { [id]: {label, description} } } }
 * `coords` holds geometry for every box you touched (existing + added).
 * `added` holds the definition for boxes you created in the editor (inspection
 * targets that are not yet in the scene file).
 * `removed` holds committed (base) hotspot ids you marked for deletion because
 * the object is not in the art; the merge drops them so they vanish live, and
 * the editor's export lists them so they can be deleted from the scene file.
 * `edited` holds label / Craine-comment (description) overrides for committed
 * hotspots; the stage applies them live, and the export lists them to update
 * the scene file.
 */
const FLAG_KEY = 'ds_hotspot_editor';
const STORE_KEY = 'ds_hotspot_overrides_v2';

/** Editor is on automatically on localhost (DEV), or anywhere the flag is set. */
export function isHotspotEditorEnabled() {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) return true;
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function loadOverrides() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveOverrides(obj) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(obj || {}));
  } catch {
    /* storage unavailable — editing simply won't persist */
  }
  // Also persist to a real file on disk (dev endpoint) so work survives a
  // server/browser restart or a cleared localStorage. No-op in production.
  saveToDisk(obj || {});
}

const DISK_ENDPOINT = '/__ds_hotspots';
let _diskTimer = null;

/**
 * Persist the full store to disk via the dev endpoint. Debounced so rapid edits
 * coalesce; pass { immediate: true } to flush now. Silently no-ops if the
 * endpoint isn't present (production) or the editor is disabled.
 */
export function saveToDisk(store, { immediate = false } = {}) {
  if (!isHotspotEditorEnabled()) return;
  const doSave = () => {
    try {
      fetch(DISK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(store || {}),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore */
    }
  };
  if (immediate) { doSave(); return; }
  if (_diskTimer) clearTimeout(_diskTimer);
  _diskTimer = setTimeout(doSave, 400);
}

/** Read the on-disk store back (dev endpoint). Returns the object or null. */
export async function loadFromDisk() {
  if (!isHotspotEditorEnabled()) return null;
  try {
    const r = await fetch(DISK_ENDPOINT, { method: 'GET' });
    if (!r.ok) return null;
    const data = await r.json();
    return data && Object.keys(data).length ? data : null;
  } catch {
    return null;
  }
}

/** Normalized { coords, added, removed, edited } entry for a scene. */
export function sceneOverride(sceneId) {
  const e = loadOverrides()[sceneId] || {};
  return {
    coords: e.coords || {},
    added: e.added || {},
    removed: e.removed || [],
    edited: e.edited || {},
  };
}

/**
 * Label / Craine-comment overrides for committed hotspots in a scene, shaped as
 * { [id]: {label?, description?} }. Empty for a normal viewer build. CinematicStage
 * spreads these over the scene's hotspot items so edited comments respond live.
 */
export function editedDefsFor(sceneId) {
  if (!isHotspotEditorEnabled()) return {};
  return sceneOverride(sceneId).edited || {};
}

/** Clear overrides for one scene, or (no arg) all scenes. */
export function clearOverrides(sceneId) {
  if (!sceneId) {
    saveOverrides({});
    return;
  }
  const all = loadOverrides();
  delete all[sceneId];
  saveOverrides(all);
}

/**
 * Committed coords for a scene, with any editor geometry overrides merged on top
 * (this also surfaces the geometry of newly authored boxes). In a normal viewer
 * build this is a no-op passthrough of the committed coords.
 */
export function mergedSceneCoords(base, sceneId) {
  const baseScene = (base && base[sceneId]) || {};
  if (!isHotspotEditorEnabled()) return baseScene;
  const { coords, removed } = sceneOverride(sceneId);
  const out = { ...baseScene };
  for (const id of Object.keys(coords)) {
    out[id] = { ...(baseScene[id] || {}), ...coords[id] };
  }
  // Marked-removed hotspots lose their geometry, so they stop rendering (the
  // stage skips any hotspot without coords). Viewers are unaffected: this only
  // runs when the editor is enabled; a real removal is committed to source.
  for (const id of removed) delete out[id];
  return out;
}

/**
 * Newly authored inspection targets for a scene, shaped as ready-to-render
 * examine hotspot items. Empty for a normal viewer build. CinematicStage merges
 * these into its hotspot list so they hover, scan, and respond live.
 */
export function addedHotspotsFor(sceneId) {
  if (!isHotspotEditorEnabled()) return [];
  const added = sceneOverride(sceneId).added;
  return Object.entries(added).map(([id, d]) => ({
    id,
    label: (d && d.label) || id,
    type: 'examine',
    description: (d && d.description) || '',
    ...(d && d.lumenReadout ? { lumenReadout: d.lumenReadout } : {}),
    ...(d && d.discovers ? { discovers: d.discovers } : {}),
    _kind: 'examine',
    _added: true,
  }));
}
